import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { consentStatus, setConsent } from "../lib/analytics";

/**
 * Minimal GDPR-aligned consent banner. Renders once until the user picks
 * "Accept" or "Decline". Decision is persisted in localStorage and respected
 * by the analytics client (silent until granted).
 */
export default function ConsentBanner() {
  const [decision, setDecision] = useState(() => consentStatus());

  useEffect(() => {
    if (!decision) {
      // Small delay so it doesn't clobber the first paint
      const t = setTimeout(() => setDecision(consentStatus()), 600);
      return () => clearTimeout(t);
    }
  }, [decision]);

  if (decision) return null;

  const handle = (choice) => {
    setConsent(choice);
    setDecision(choice);
    if (choice === "granted") {
      // Fire one immediate pageview so today's session is captured
      try {
        // Lazy import — avoids circular at module-load time
        import("../lib/analytics").then(({ trackPageview }) => {
          trackPageview(window.location.pathname);
        });
      } catch { /* noop */ }
    }
  };

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      data-testid="consent-banner"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] w-[min(720px,92vw)]
                 rounded-xl border border-brand-border bg-white shadow-lg p-5 flex items-start gap-4
                 animate-in fade-in slide-in-from-bottom-4 duration-500"
    >
      <div className="hidden sm:flex h-10 w-10 shrink-0 items-center justify-center
                      rounded-lg bg-brand-bg border border-brand-border text-brand-accent">
        <Cookie className="w-5 h-5" />
      </div>
      <div className="flex-1 text-sm text-brand leading-relaxed">
        <p className="font-semibold mb-1">We use first-party analytics</p>
        <p className="text-[#5C5C59]">
          Infralytics stores anonymised page-visit + action data on our own servers
          (no third-party cookies, no ad networks) to improve the platform.
          See our{" "}
          <Link
            to="/privacy"
            className="underline text-brand-accent hover:opacity-80"
            data-testid="consent-privacy-link"
          >
            Privacy Notice
          </Link>{" "}and{" "}
          <Link
            to="/terms"
            className="underline text-brand-accent hover:opacity-80"
            data-testid="consent-terms-link"
          >
            Terms of Use
          </Link>.
        </p>
      </div>
      <div className="flex flex-col gap-2 shrink-0">
        <button
          data-testid="consent-accept"
          onClick={() => handle("granted")}
          className="rounded-md bg-brand-accent text-white text-xs font-semibold px-4 py-2 hover:opacity-90"
        >
          Accept
        </button>
        <button
          data-testid="consent-decline"
          onClick={() => handle("denied")}
          className="rounded-md border border-brand-border text-brand text-xs font-medium px-4 py-2 hover:bg-brand-bg"
        >
          Decline
        </button>
      </div>
      <button
        aria-label="Dismiss"
        onClick={() => handle("denied")}
        className="absolute top-2 right-2 text-[#9c9c99] hover:text-brand"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
